import { action, computed, observable, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { Logger, Services } from '../../services';
import { InputEventBindContext } from '../../services/input/event.context';
import { CSS, MDL } from './modal.style';

export interface ModalResponse {
	ok: boolean;
	cancel: boolean;
}

export interface ModalContext {
	/** Title for the modal */
	title: string;

	/**  Content of the modal */
	content: (() => string | MithrilVNode) | string | MithrilVNode;

	/** Whether or not to show the cancel button */
	hasCancel?: boolean;

	/** Array of buttons to display*/
	actions?: (() => MithrilVNode) | MithrilVNode;

	/** Title for OK button */
	okName?: T;

	/** Callback when the modal's form has been submitted. */
	onSubmit?: () => void;

	/** Custom class to append to the container element */
	className?: string;

	/** Function to decide whether the modal can be closed (for example while loading) */
	canClose?: () => boolean;
}

export class ModalServiceImpl {
	CSS = CSS;

	@observable private current: { resolve; reject; ctx: ModalContext } | null = null;
	_bindCtx: InputEventBindContext | null = null;

	constructor() {
		makeObservable(this);
	}

	@computed get isOpen() {
		return this.current != null;
	}

	@action open(ctx: ModalContext): Promise<ModalResponse> {
		//We want to make sure the filter is empty when opening a new modal
		Services.MultiListSearch.reset();

		if (this.isOpen) {
			let currentTitle = '';
			if (this.current != null) {
				currentTitle = this.current.ctx.title;
			}

			this.close(false);
			Logger.fatal({ newModal: ctx.title, old: currentTitle }, 'Open modal over existing modal');
		}

		this.eventContext.on(this.eventContext.Code.Escape, () => {
			if (ctx.canClose != null && ctx.canClose()) {
				this.close();
			}
		});

		return new Promise((resolve, reject) => (this.current = { resolve, reject, ctx }));
	}

	@action setTitle(title: string) {
		if (this.current == null) {
			Logger.fatal('ModalService.setTitle called when modal is not open');
			return;
		}
		this.current.ctx.title = title;
	}

	get eventContext(): InputEventBindContext {
		if (this._bindCtx == null) {
			this._bindCtx = Services.Input.getContext();
		}
		return this._bindCtx;
	}

	/**
	 * Force close the current modal.
	 *
	 * This will resolve the open promise with the same action as "cancel being clicked"
	 */
	@action close(isOk?: boolean) {
		if (this.eventContext) {
			this.eventContext.destroy();
		}

		if (!this.isOpen) {
			return null;
		}

		const cancel = isOk == null || isOk === false;
		const ok = !cancel;

		const openPromise = this.current;
		this.current = null;
		openPromise?.resolve({ ok, cancel });
		return false;
	}

	view() {
		if (!this.isOpen) {
			return null;
		}

		const ctx = this.current!.ctx;
		let content;
		if (typeof ctx.content === 'function') {
			content = ctx.content();
		} else {
			content = ctx.content;
		}

		const onsubmit = (e: Event) => {
			if (typeof ctx.onSubmit === 'function') {
				ctx.onSubmit();
			}
			e.preventDefault();
			return false;
		};

		return m('div', { className: CSS.container + ' ' + CSS.isOpen }, [
			m('form', { className: `${CSS.modal} ${ctx.className || ''}`, onsubmit }, [
				m('div', { className: CSS.title }, Translate(ctx.title)),
				m('div', { className: CSS.content }, content),
				this.viewActions,
			]),
		]);
	}

	@computed get viewActions() {
		const ctx = this.current!.ctx;

		if (ctx.actions) {
			return m('div', { className: CSS.actions }, typeof ctx.actions === 'function' ? ctx.actions() : ctx.actions);
		}

		const onCancelClick = (_) => this.close(false);
		const onOkClick = (_) => this.close(true);

		const buttons: MithrilVNode[] = [
			m(
				'button',
				{
					className: MDL.button + ' ' + CSS.action.ok,
					onclick: onOkClick,
					type: 'submit',
					id: 'modal-ok',
				},
				Translate(ctx.okName || T.OK)
			),
		];

		if (ctx.hasCancel) {
			buttons.unshift(
				m(
					'button',
					{
						className: MDL.button + ' ' + CSS.action.cancel,
						onclick: onCancelClick,
						type: 'button',
					},
					Translate(T.CANCEL)
				)
			);
		}

		return m('div', { className: CSS.actions }, buttons);
	}
}

export const ModalService = new ModalServiceImpl();
